import { PriceType, ProductType, SupportedCurrency, SupportedLanguage } from "interfaces";
import moment from "moment";
import { languageSchema } from "components/shared/event/schema";
import i18n from "utills/i18n";
import { z } from "zod";

export const ParentTicketSchema = (languages: SupportedLanguage[]) => {
  return z
    .discriminatedUnion("is_package", [
      z.object({
        is_package: z.literal(true),
        category: languageSchema(languages),
        selling_min_qty: z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
            required_error: i18n.t(
              "validation.errorMessage.thisFieldIsRequired"
            ),
          })
          .min(1, {
            message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
          })
          .nonnegative({
            message: i18n.t("validation.errorMessage.nonNegativeNum"),
          }),
        selling_max_qty: z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
            required_error: i18n.t(
              "validation.errorMessage.thisFieldIsRequired"
            ),
          })
          .min(1, {
            message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
          })
          .nonnegative({
            message: i18n.t("validation.errorMessage.nonNegativeNum"),
          }),
        selling_type: z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        attendant_template: z
          .array(
            z.object({
              template_id: z
                .string()
                .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
              name: z.object({
                en: z.string().optional(),
                vi: z.string().optional(),
              }),
            })
          )
          .min(
            2,
            i18n.t("validation.errorMessage.minimumForPackageTicket", {
              min: 2,
            })
          ),
        is_use_private_flow: z.boolean(),
        is_use_actiup_private_flow: z.boolean(),
      }),
      z.object({
        is_package: z.literal(false),
        category: languageSchema(languages),
        selling_min_qty: z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
            required_error: i18n.t(
              "validation.errorMessage.thisFieldIsRequired"
            ),
          })
          .min(1, {
            message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
          })
          .nonnegative({
            message: i18n.t("validation.errorMessage.nonNegativeNum"),
          }),
        selling_max_qty: z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
            required_error: i18n.t(
              "validation.errorMessage.thisFieldIsRequired"
            ),
          })
          .min(1, {
            message: i18n.t("validation.errorMessage.minNum", { min: 1 }),
          })
          .nonnegative({
            message: i18n.t("validation.errorMessage.nonNegativeNum"),
          }),
        selling_type: z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        attendant_template: z
          .array(
            z.object({
              template_id: z
                .string()
                .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
              name: z.object({
                en: z.string().optional(),
                vi: z.string().optional(),
              }),
            })
          )
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        is_use_private_flow: z.boolean(),
        is_use_actiup_private_flow: z.boolean(),
      }),
    ])
    .refine((data) => data.selling_min_qty <= data.selling_max_qty, {
      message: i18n.t("validation.errorMessage.cannotBeSmaller", {
        field: i18n.t(
          "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
        ),
      }),
      path: ["selling_max_qty"],
    })
    .superRefine(({ selling_min_qty, selling_max_qty }, ctx) => {
      if (selling_max_qty < selling_min_qty) {
        ctx.addIssue({
          path: ["selling_max_qty"],
          code: "custom",
          message: i18n.t("validation.errorMessage.cannotBeSmaller", {
            field: i18n.t(
              "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
            ),
          }),
        });
      }
    });
};

export const SubTicketSchema = (languages: SupportedLanguage[], currency: SupportedCurrency) => {
  console.log(currency);
  const ticketSchema = (priceType: PriceType) => {
    return z.object({
      price_type: z.literal(priceType),
      type_name: languageSchema(languages),
      short_description: z.object({
        en: z.string().optional(),
        vi: z.string().optional(),
      }),
      original_price:
        priceType === PriceType.PAID
          ? z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .min(currency === SupportedCurrency.VND ? 10000 : 0, {
                message: i18n.t("validation.errorMessage.minNum", {
                  min: currency === SupportedCurrency.VND ? 10000 : 0,
                }),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              })
          : z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              }),
      price:
        priceType === PriceType.PAID
          ? z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .min(currency === SupportedCurrency.VND ? 10000 : 0, {
                message: i18n.t("validation.errorMessage.minNum", {
                  min: currency === SupportedCurrency.VND ? 10000 : 0,
                }),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              })
          : z
              .number({
                invalid_type_error: i18n.t(
                  "validation.errorMessage.mustBeANumber"
                ),
                required_error: i18n.t(
                  "validation.errorMessage.thisFieldIsRequired"
                ),
              })
              .nonnegative({
                message: i18n.t("validation.errorMessage.nonNegativeNum"),
              }),
      sell_from: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      sell_to: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      hide_in_ui: z.boolean(),
      is_accept_edit_participant_info: z.boolean(),
      is_accept_upgrade_ticket: z.boolean(),
      is_accept_transfer_ticket: z.boolean(),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      ticket_phase: z.union([
        z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        z.number({
          invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
        }),
      ]),
      total_for_sell: z
        .union([
          z
            .number()
            .int()
            .min(0, i18n.t("validation.errorMessage.minNum", { min: 0 }))
            .nullable(),
          z.nan(),
        ])
        .optional(),
    });
  };

  return z
    .discriminatedUnion("price_type", [
      ticketSchema(PriceType.FREE),
      ticketSchema(PriceType.PAID),
    ])
    .refine(
      (data) => moment(data.sell_from).isSameOrBefore(moment(data.sell_to)),
      {
        message: "End date cannot be earlier than start date.",
        path: ["sell_to"],
      }
    )
    .refine((data) => data.original_price >= data.price, {
      message: "Original price cannot be smaller than sell price.",
      path: ["original_price"],
    });
};
